'use client';

import { ReplyErrorCodes, ReplyReportReasonType } from '@diritto/common/reply/constants';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import styled from '@emotion/styled';
import axios, { AxiosError } from 'axios';
import { ButtonType } from 'constants/button';
import { isPC } from 'constants/layout';
import { ModalParams, ModalType } from 'constants/modal';
import { useReportReplyMutation } from 'queries/reply/mutations/useReportReplyMutation';
import { useCallback, useEffect, useState } from 'react';
import { Button } from 'ui-components/common/Button';
import { RadioButton } from 'ui-components/common/RadioButton';
import { TextArea } from 'ui-components/common/TextArea';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { useMedia } from 'ui-hooks/useMediaQuery';
import { useShowModal } from 'ui-hooks/useShowModal';
import { showToast } from 'utils/modal';
import { ModalHandler } from 'utils/ModalHandler';
import { SentryService } from 'utils/SentryService';

export const ReplyReportModal = () => {
  const { isPc } = useMedia();

  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const [state, setState] = useState<ModalParams[ModalType.ReplyReport]>();
  const [reason, setReason] = useState<ReplyReportReasonType>();
  const [reasonDescription, setReasonDescription] = useState<string>();

  const handleHide = useCallback(() => {
    hideModal();
    setReason(undefined);
    setReasonDescription(undefined);
  }, [hideModal]);

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    handleHide();
  }, [handleHide, state]);

  const { mutate: reportMutate } = useReportReplyMutation({ onSettled: state?.onSettled });

  const handleSubmit = useCallback(async () => {
    if (!state?.replyId || !reason) {
      return;
    }

    reportMutate(
      { replyId: state?.replyId, payload: { reason, reasonDescription } },
      {
        onSuccess: () => {
          showToast('신고가 접수되었어요.');
        },
        onError: (e: any) => {
          if (axios.isAxiosError(e)) {
            const errorCode = (e as AxiosError<{ code: ReplyErrorCodes }>).response?.data?.code;
            if (errorCode === ReplyErrorCodes.ReportAlreadyExist) {
              showToast(`이미 신고한 댓글이에요.`);
              return;
            }
          } else {
            // Axios error 아닌 경우에만 보내줌
            SentryService.captureException(e);
          }
          showToast('일시적인 오류로 신고하지 못했어요. 잠시 뒤 다시 시도해주세요.');
        },
      },
    );
    handleHide();
  }, [handleHide, reason, reasonDescription, reportMutate, state]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.ReplyReport]) => {
      setState(params);

      if (show) {
        showModal();
      } else {
        handleHide();
      }
    };

    ModalHandler.addEventListener(ModalType.ReplyReport, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.ReplyReport);
    };
  }, [handleHide, showModal]);

  return (
    <ModalLayout isShow={isShow} revealModal={revealModal} isBottom={!isPc}>
      <StyledModal>
        <StyledDescriptionTitle>댓글 / 답글 신고</StyledDescriptionTitle>
        <StyledRadioGuideMessage>
          적합하지 않은 신고나 허위 신고가 누적될 경우 제재를 받을 수 있어요.
        </StyledRadioGuideMessage>
        <StyledRadioWrapper>
          <StyledRadioButton
            id="RepeatedlySameMessage"
            showName
            name="같은 내용을 여러 곳에 기계적으로 도배"
            value={ReplyReportReasonType.RepeatedlySameMessage}
            selectedValue={reason}
            onChange={() => setReason(ReplyReportReasonType.RepeatedlySameMessage)}
          />
          <StyledRadioButton
            id="SoullessMessage"
            showName
            name="리워드만을 목적으로 하는 영혼 없는 글"
            value={ReplyReportReasonType.SoullessMessage}
            selectedValue={reason}
            onChange={() => setReason(ReplyReportReasonType.SoullessMessage)}
          />
          <StyledRadioButton
            id="AuthorObstruction"
            showName
            name="그 외 작가님의 작품 활동을 방해하는 글"
            value={ReplyReportReasonType.AuthorObstruction}
            selectedValue={reason}
            onChange={() => setReason(ReplyReportReasonType.AuthorObstruction)}
          />
          <StyledRadioButton
            id="Slander"
            showName
            name="욕설 / 비방 / 차별 / 혐오"
            value={ReplyReportReasonType.Slander}
            selectedValue={reason}
            onChange={() => setReason(ReplyReportReasonType.Slander)}
          />
          <StyledRadioButton
            id="Spam"
            showName
            name="영리목적 / 홍보성 / 스팸"
            value={ReplyReportReasonType.Spam}
            selectedValue={reason}
            onChange={() => setReason(ReplyReportReasonType.Spam)}
          />
          <StyledRadioButton
            id="ExposurePersonalInformation"
            showName
            name="개인정보 노출"
            value={ReplyReportReasonType.ExposurePersonalInformation}
            selectedValue={reason}
            onChange={() => setReason(ReplyReportReasonType.ExposurePersonalInformation)}
          />
          <StyledRadioButton
            id="SexualHarassment"
            showName
            name="성적 모욕 / 불쾌한 표현"
            value={ReplyReportReasonType.SexualHarassment}
            selectedValue={reason}
            onChange={() => setReason(ReplyReportReasonType.SexualHarassment)}
          />
          <StyledRadioButton
            id="CopyrightInfringement"
            showName
            name="저작권 침해"
            value={ReplyReportReasonType.CopyrightInfringement}
            selectedValue={reason}
            onChange={() => setReason(ReplyReportReasonType.CopyrightInfringement)}
          />
          <StyledRadioButton
            id="Other"
            showName
            name="기타 사유"
            value={ReplyReportReasonType.Other}
            selectedValue={reason}
            onChange={() => setReason(ReplyReportReasonType.Other)}
          />
        </StyledRadioWrapper>
        {reason === ReplyReportReasonType.Other && (
          <StyledCommentTextArea
            placeholder="기타 사유를 작성해 주세요. (500자 제한)"
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReasonDescription(e.target.value)}
          />
        )}
        <StyledButtons>
          <StyledButton label="신고하기" onClick={handleSubmit} disabled={!reason} />
          <StyledButton label="닫기" buttonType={ButtonType.SecondaryNoBorder} onClick={handleClose} />
        </StyledButtons>
      </StyledModal>
    </ModalLayout>
  );
};

const StyledModal = styled.div`
  width: 100vw;
  border-radius: 16px 16px 0 0;
  padding: 32px 24px 0 24px;

  @media (${isPC}) {
    width: 400px;
    border-radius: 16px;
    padding: 32px 24px 16px 24px;
  }
  background-color: ${Color.White};

  display: flex;
  flex-direction: column;
  align-items: center;
  ${SafeAreaBottomStyles(16)}
`;

const StyledDescriptionTitle = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
  color: ${Color.Black};
  text-align: left;
  width: 100%;
`;

const StyledRadioGuideMessage = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin: 8px 0;
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
  color: ${Color.Gray500};
`;

const StyledRadioWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledRadioButton = styled(RadioButton)`
  display: flex;
  align-items: center;
  height: 40px;
`;

const StyledButtons = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledCommentTextArea = styled(TextArea)`
  width: 100%;
  height: 48px;
  border: 1px solid ${Color.Gray400};
  border-radius: 4px;
  padding: 13px 16px;
  margin-top: 4px;
  ${Typography.XXSmall};

  &:focus {
    outline: none;
    border: 1px solid ${Color.Primary500};
  }
`;
